import type React from 'react';
import { Children, isValidElement } from 'react';
import { cn } from '@/lib/utils';

type FormProps = {
  id: string;
  title?: string;
  description?: string;
  className?: string;
  autoComplete?: string;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  children?: React.ReactNode;
};

/**
 * Card de formulário no padrão do painel admin: cabeçalho com título display + frase,
 * corpo em grid auto-fit de 280px, rodapé sobre `bg-sunken`.
 */
const Form: React.FC<FormProps> = ({
  id,
  title,
  description,
  className,
  autoComplete = 'off',
  onSubmit,
  children,
}) => {
  let content: React.ReactNode = null;
  let footer: React.ReactNode = null;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === FormContent) content = child;
    if (child.type === FormFooter) footer = child;
  });

  return (
    <section className={cn('overflow-hidden rounded-[16px] border border-border bg-card', className)}>
      {(title || description) && (
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-[26px] py-5">
          <span className="flex min-w-0 flex-col gap-1.5">
            {title && (
              <span className="font-display text-[18px] font-extrabold uppercase tracking-tight text-white">
                {title}
              </span>
            )}
            {description && <span className="text-[13px] text-muted-foreground">{description}</span>}
          </span>
        </div>
      )}

      <form noValidate autoComplete={autoComplete} id={id} onSubmit={onSubmit}>
        {content}
      </form>

      {footer}
    </section>
  );
};

type FormContentProps = {
  className?: string;
  children?: React.ReactNode;
};

const FormContent: React.FC<FormContentProps> = ({ className, children }) => {
  return (
    <div
      className={cn(
        'grid grid-cols-[repeat(auto-fit,minmax(min(100%,280px),1fr))] gap-4 p-[26px]',
        className,
      )}
    >
      {children}
    </div>
  );
};

type FormGroupProps = {
  title?: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
};

/** Bloco secundário dentro de um `Form` (ex.: "Despesas e receitas"), com título próprio. */
const FormGroup: React.FC<FormGroupProps> = ({ title, description, actions, children }) => {
  return (
    <div className="col-span-full -mx-[26px] -mt-4 border-t border-border">
      {(title || description || actions) && (
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-[26px] py-5">
          <span className="mr-auto flex min-w-0 flex-col gap-1.5">
            {title && (
              <span className="font-display text-[18px] font-extrabold uppercase tracking-tight text-white">
                {title}
              </span>
            )}
            {description && <span className="text-[13px] text-muted-foreground">{description}</span>}
          </span>
          {actions}
        </div>
      )}
      <div className="p-[26px]">{children}</div>
    </div>
  );
};

type FormFooterProps = {
  children?: React.ReactNode;
};

const FormFooter: React.FC<FormFooterProps> = ({ children }) => {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2.5 border-t border-border bg-sunken px-[26px] py-[18px]">
      {children}
    </div>
  );
};

export { Form, FormContent, FormFooter, FormGroup };
